import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Kpi } from '@/types/dashboards';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 });

/** `Kpi.name` is `"station:{station_id}"` — strip the prefix for display. */
function stationId(kpiName: string): string {
  return kpiName.startsWith('station:') ? kpiName.slice('station:'.length) : kpiName;
}

export interface StationRiskTableProps {
  kpis: Kpi[] | undefined;
  isLoading: boolean;
  error: Error | null;
  emptyMessage?: string;
}

/**
 * Station Risk table — `GET /api/v1/dashboards/station-risk`. Top-20
 * stations by within-brand fraud-rate percentile over a trailing 90-day
 * window, already ordered by the backend (percentile desc, alert count
 * desc). Rendered as a table (one row per station) rather than a card
 * grid — 20 rows scan faster as a table than as cards, and the ordering
 * is meaningful (worst-first).
 *
 * The wire shape doesn't carry `name`/`brand`/`cross_vehicle_reuse` as
 * separate fields — they're folded into `story` (Task 3's flat KPI
 * contract). The "Watchlist" column is a display-only heuristic that
 * checks for the backend's own literal "NEW STATION WATCHLIST" marker in
 * `story` — it is not re-derived client-side, just surfaced as a badge.
 */
export function StationRiskTable({
  kpis,
  isLoading,
  error,
  emptyMessage = 'No stations with transaction volume in the trailing 90 days.',
}: StationRiskTableProps): JSX.Element {
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-3 text-sm text-red-800">
          Could not load station risk: {error.message}
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!kpis || kpis.length === 0) {
    return (
      <Card>
        <CardContent className="py-3 text-sm text-muted-foreground">{emptyMessage}</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Station</TableHead>
              <TableHead className="text-right">Fraud-rate percentile</TableHead>
              <TableHead>Watchlist</TableHead>
              <TableHead>Story</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {kpis.map((kpi) => {
              const isWatchlist = kpi.story.includes('NEW STATION WATCHLIST');
              return (
                <TableRow key={kpi.name}>
                  <TableCell className="font-mono text-xs">{stationId(kpi.name)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {typeof kpi.value === 'number' ? NUM.format(kpi.value) : (kpi.value ?? '—')}
                    {kpi.unit ? ` ${kpi.unit}` : ''}
                  </TableCell>
                  <TableCell>
                    {isWatchlist ? (
                      <Badge className="border-transparent bg-red-100 text-red-800">
                        Watchlist
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="max-w-xl text-sm text-muted-foreground">
                    {kpi.story}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default StationRiskTable;
